import { useCallback, useEffect, useRef } from 'react';

import { AllWalletsRows } from '@/features/wallets/components/AllWallets/AllWAlletsRows';
import { AllWalletsHeaders } from '@/features/wallets/components/AllWallets/AllWAlletsHeaders';
import { isWalletsSortKeyAvailable, ISearchWallet } from '@/features/wallets/store/models/wallet.model';
import { ALL_WALLETS_PER_PAGE } from '@/features/wallets/constants';
import {
  useAllWalletsFilters,
  parseAllWalletsFiltersValue,
} from '@/features/wallets/hooks/AllWallets/useAllWalletsFilters';
import { searchAgentWallets } from '@/features/wallets/store/actions/agent-wallets.actions';
import {
  TAllWalletsFiltersFields,
  IAllWalletsFiltersFormFields,
} from '@/features/wallets/store/types/agent-wallets.types';
import { useAppDispatch, useAppSelector } from '@/hooks/redux-helpers';
import { parseAllParameters, periodBases, TUseTableDataReturn } from '@/ui-kit/table-filters/filters.type';
import { allWalletsHeadersList, TAllWalletsRowType } from '@/features/wallets/store/models/allWallets.model';
import { useConfigWalletIdsList } from '@/features/wallets/hooks/useConfigWalletIdsList';

export function useAllWalletsTableData(
  defaultFilters: Partial<IAllWalletsFiltersFormFields> = {},
): TUseTableDataReturn<TAllWalletsRowType, TAllWalletsFiltersFields> {
  const dispatch = useAppDispatch();

  const hasData = useAppSelector((state) => state.agentWallets.paginateWallets.elements).length > 0;
  const profileWallets = useAppSelector((state) => state.agentWallets.paginateWallets.elements);
  const wallets = profileWallets.map((pw) => pw.wallet);
  const pagination = useAppSelector((state) => state.agentWallets.paginateWallets.pagination);
  const isLoading = useAppSelector((state) => state.agentWallets.isLoading);
  const error = useAppSelector((state) => state.agentWallets.error);
  const walletTypeIdList = useConfigWalletIdsList();

  const defaultFiltersValues: IAllWalletsFiltersFormFields = {
    typeId: walletTypeIdList[0],
    createdDate: {
      from: null,
      to: null,
      base: periodBases.ON,
    },
    ...defaultFilters,
  };

  const queryParameters = useRef<{ pageNumber: number; filters: ISearchWallet }>({
    pageNumber: undefined,
    filters: parseAllParameters<IAllWalletsFiltersFormFields, ISearchWallet>(
      defaultFiltersValues,
      parseAllWalletsFiltersValue,
    ),
  });

  const filtersDeps = JSON.stringify(defaultFiltersValues);

  useEffect(() => {
    const filters = JSON.parse(filtersDeps);
    queryParameters.current = {
      pageNumber: 1,
      filters: parseAllParameters<IAllWalletsFiltersFormFields, ISearchWallet>(filters, parseAllWalletsFiltersValue),
    };
    dispatch(
      searchAgentWallets({
        pageNumber: queryParameters.current.pageNumber,
        ...queryParameters.current.filters,
      }),
    );
  }, [dispatch, filtersDeps]);

  const onPageChange = useCallback(
    (pageNumber: number) => {
      queryParameters.current = {
        ...queryParameters.current,
        pageNumber,
      };
      const { filters, ...parameters } = queryParameters.current;
      dispatch(searchAgentWallets({ ...parameters, ...filters }));
    },
    [dispatch],
  );

  const handleFilterChange = useCallback(
    (filtersParameters: ISearchWallet) => {
      queryParameters.current = {
        ...queryParameters.current,
        filters: filtersParameters,
        pageNumber: 1,
      };
      const { filters, ...parameters } = queryParameters.current;
      dispatch(searchAgentWallets({ ...parameters, ...filters }));
    },
    [dispatch],
  );

  const { clearFilters, hasFilters, status, typeId, createdDate, search } = useAllWalletsFilters(
    handleFilterChange,
    defaultFiltersValues,
  );

  const onSort = useCallback((field: keyof TAllWalletsRowType) => {
    if (isWalletsSortKeyAvailable(field)) {
      // To fill
    }
  }, []);

  const onPerPage = undefined;
  const perPage = ALL_WALLETS_PER_PAGE;

  const headers = AllWalletsHeaders({ headersList: allWalletsHeadersList });
  const rows = AllWalletsRows({ profileWallets, isLoading });

  return {
    data: wallets,
    pagination,
    isLoading,
    hasData,
    error,
    headers,
    rows,
    onPageChange,
    clearFilters,
    onSort,
    onPerPage,
    perPage,
    fields: { status, typeId, createdDate, search },
    hasFilters,
  };
}
